import { useMemo } from "react"
import { isEmpty } from "lodash"
import moment from "moment-timezone"

import { useSearchParams } from "@/hooks/useSearchParams"
import {
  getDecimalPoint,
  getMetricValueStr,
  getSummaryImplValue,
  parseSummary,
  parseUnit,
} from "@/app/report/common/tools"
import { DIVE_METRICS, REPORT_TYPE } from "@/app/report/common/constants"
import {
  buildGroupDisplayColumns,
  getColumnDataKey,
  getColumnTitleKey,
} from "@/app/report/common/customTableColumns"

// 그룹·컬럼 정의(순수)는 common/customTableColumns 에 있다 — 기존 사용처 호환을 위해 여기서도 내보낸다.
export {
  GROUP_KEY_MAPPING,
  PLAYER_GROUP_COLUMNS,
  TREND_PLAYER_GROUP_COLUMNS,
  ACTIVITY_GROUP_COLUMNS,
  isMultiColumnGroup,
  getGroupColumns,
  getColumnTitleKey,
  buildGroupDisplayColumns,
} from "@/app/report/common/customTableColumns"
import { MetricMapType, StatsSettings, SummaryRowsSetting } from "@/app/report/common/type"
import { useCalculatedMetricsQuery, useUnitQuery } from "@/hooks/api/useSettingQuery"
import { useMetricList } from "@/hooks/api/useMetricQuery"
import {
  useDailyActivityGroupSummaryQuery,
  useDailySessionGroupSummaryQuery,
  useTrendGroupSummaryQuery,
} from "@/hooks/api/useReportQuery"
import { ActivityType } from "@/types/activity"
import { TrendGroupSummaryRow } from "@/types/report"
import { CalculatedMetric, Unit } from "@/types/setting"
import { SummaryImpl } from "@/app/report/common/constructor"
import { aggregateSummaryRows } from "@/app/report/common/summaryAggregate"
import { ColumnFilterMap, isColumnFilterActive } from "@/stores/reportColumnFilter"
import type { TFunction } from "i18next"
import { globalDate } from "@/utils/format"
import { localeStringToNumber, parseMatchDay, parseMonth, parseSeasonWeek } from "@/utils/helper"
import { MONTH_NAMES } from "@/constants/date"
import { ACTIVITY_TYPE_ORDER, activityType as activityTypeKeyByName } from "@/constants/activity"
import type { ReportSortSetting } from "@/stores/reportSort"

// Custom Table 의 데이터(헤더/바디) 구성 로직.
// 화면(CustomTableSection)과 PDF(PrintableCustomTableSection)가 공유한다.

const TIME_COLUMN_KEYS = [
  "activityStartTime",
  "activityFinishTime",
  "sessionStartTime",
  "sessionFinishTime",
]

const TIME_TO_DATE_KEY: Record<string, string> = {
  activityStartTime: "activityStartDate",
  activityFinishTime: "activityFinishDate",
  sessionStartTime: "sessionStartDate",
  sessionFinishTime: "sessionFinishDate",
}

export type TableHeader = {
  key: string
  title: string
  unit: string | null
  decimalPoint: number
  indexColumn: number
  isFixed: boolean
}

// ─── 데이터 구성 순수 빌더 (화면·PDF 훅과 일괄 CSV 가 공유 — WEB-2229 Custom Table) ───

export function buildCustomTableDisplayColumns(
  settings: StatsSettings,
  reportType?: REPORT_TYPE | string,
): string[] {
  const timeColumnsFromMetrics = settings.metrics.filter((key) => TIME_COLUMN_KEYS.includes(key))
  const columns = buildGroupDisplayColumns(settings.groupKey1, settings.groupKey2, reportType)

  columns.push(...timeColumnsFromMetrics)

  return columns
}

export function buildCustomTableHeader(
  settings: StatsSettings,
  displayColumns: string[],
  metricMap: MetricMapType,
  selectedUnitList?: Unit[],
): TableHeader[] {
  const headers: TableHeader[] = []
  const pinnedColumns = settings.fixedColumns || []

  displayColumns.forEach((column, index) => {
    const isFixed = pinnedColumns.includes(column)
    headers.push({
      key: column,
      title: getColumnTitleKey(column),
      unit: null,
      decimalPoint: 0,
      indexColumn: index,
      isFixed,
    })
  })

  settings.metrics
    .filter((key) => !TIME_COLUMN_KEYS.includes(key))
    .forEach((metricSelected, index) => {
      const unit = parseUnit(metricSelected, selectedUnitList, metricMap) ?? null
      const indexMetric = index + displayColumns.length
      let decimalPoint = getDecimalPoint(metricSelected, unit)

      if (settings.calcType === "average" && unit === "times") {
        decimalPoint = 1
      }

      headers.push({
        key: metricSelected,
        title: "metric." + metricSelected,
        unit,
        decimalPoint,
        indexColumn: indexMetric,
        isFixed: false,
      })
    })

  return headers
}

export function buildCustomTableBody(
  settings: StatsSettings,
  groupSummaryData: TrendGroupSummaryRow[],
  tableHeader: TableHeader[],
  displayColumns: string[],
  metricMap: MetricMapType,
): Array<Array<string | number | null>> {
  const { calcType } = settings
  const body: Array<Array<string | number | null>> = []

  groupSummaryData.forEach((item: TrendGroupSummaryRow) => {
    const row: Array<string | number | null> = []

    displayColumns.forEach((column) => {
      const dataKey = TIME_TO_DATE_KEY[column] || getColumnDataKey(column)
      const value = item[dataKey as keyof TrendGroupSummaryRow]
      if (TIME_COLUMN_KEYS.includes(column)) {
        row.push(value ? moment(value as string).format("YYYY-MM-DD HH:mm:ss") : null)
      } else if (
        column === "activityStartDate" ||
        column === "activityFinishDate" ||
        column === "sessionStartDate" ||
        column === "sessionFinishDate" ||
        column === "date"
      ) {
        row.push(value ? globalDate(value as string, true) : null)
      } else if (column === "activityType") {
        row.push(
          (ActivityType[value as string as keyof typeof ActivityType] || value) as
            | string
            | number
            | null,
        )
      } else if (column === "week" && value) {
        const [year, weekNum] = String(value).split("-")
        row.push(`${year} W${parseInt(weekNum, 10)}`)
      } else if (column === "month" && value) {
        const [year, monthNum] = String(value).split("-")
        const monthIndex = parseInt(monthNum, 10) - 1
        row.push(`${year} ${MONTH_NAMES[monthIndex] || monthNum}`)
      } else {
        row.push(value as string | number | null)
      }
    })

    const sourceRaw = calcType === "total" ? item.total : item.average
    const source = parseSummary(sourceRaw)

    const isGK = isCustomTableRowGK(settings, item)

    tableHeader.slice(displayColumns.length).forEach((header) => {
      if (!isGK && DIVE_METRICS.includes(header.key)) {
        row.push(null)

        return
      }

      const { value } = getSummaryImplValue(header.key, source, metricMap)
      const unit = header.unit

      row.push(
        value != null
          ? getMetricValueStr({
              value: value as number,
              unit,
              decimalPoint: header.decimalPoint || 0,
              calcType,
              showUnit: false,
            })
          : null,
      )
    })

    body.push(row)
  })

  return body
}

export type CustomTableDataOptions = {
  reportType?: REPORT_TYPE
  // Daily 세션 모드: 이 세션들만 집계한다(리포트 필터 세션 ∩ 섹션 내 선택). 없으면 액티비티 전체 집계.
  sessionMode?: boolean
  sessionIds?: number[]
  // 집계 대상 액티비티·선수. 리포트 화면은 URL(idfActivity·idfPlayer)에서 읽지만
  // PDF 빌더는 URL 파라미터가 없어 Report Filters 값을 직접 넘긴다 (#75). 넘기면 URL 보다 우선.
  activityIds?: number[]
  playerIds?: number[]
}

export function useCustomTableData(settings: StatsSettings, options: CustomTableDataOptions = {}) {
  const { reportType, sessionMode = false, sessionIds } = options
  const searchParams = useSearchParams()

  const { data: metricList } = useMetricList()
  const { data: selectedUnitList } = useUnitQuery()
  // 커스텀 계산 메트릭 수식 — 요약 행에서 서버처럼 요약값에 수식을 재적용하기 위해 필요 (#98)
  const { data: calculatedMetrics } = useCalculatedMetricsQuery()

  const { activityIds, playerIds } = options
  const idfActivityList = useMemo(
    () => activityIds ?? searchParams.get("idfActivity")?.split(",").map(Number) ?? [],
    [activityIds, searchParams],
  )
  const idfPlayerList = useMemo(
    () => playerIds ?? searchParams.get("idfPlayer")?.split(",").map(Number) ?? [],
    [playerIds, searchParams],
  )

  // 모드별로 집계 기준이 다르다 (#99-1, #104)
  //   Daily 세션 모드   : 선택한 세션만 — 세션 크롭 반영
  //   Daily 액티비티 모드: 액티비티 전체 — 세션 크롭 미반영 (Action Map·Stats 와 같은 기준)
  //   Trend            : 세션 누적 집계 (기존 유지)
  // trend/group 은 세션 요약을 읽어 크롭이 반영되므로 Daily 액티비티 모드에 쓰면 안 된다.
  const useSessionQuery = sessionMode && !!sessionIds?.length
  const useDailyActivityQuery = reportType === REPORT_TYPE.DAILY && !useSessionQuery
  const { data: trendGroupSummaryData, isLoading: trendGroupSummaryLoading } =
    useTrendGroupSummaryQuery(
      {
        idfActivityList,
        idfPlayerList,
        groupKey1: settings.groupKey1 || "",
        groupKey2: settings.groupKey2,
      },
      !useSessionQuery &&
        !useDailyActivityQuery &&
        !!idfActivityList?.length &&
        !!idfPlayerList?.length &&
        !!settings.groupKey1,
    )
  const { data: dailyActivityGroupSummaryData, isLoading: dailyActivityGroupSummaryLoading } =
    useDailyActivityGroupSummaryQuery(
      {
        idfActivityList,
        idfPlayerList,
        groupKey1: settings.groupKey1 || "",
        groupKey2: settings.groupKey2,
      },
      useDailyActivityQuery &&
        !!idfActivityList?.length &&
        !!idfPlayerList?.length &&
        !!settings.groupKey1,
    )
  const { data: sessionGroupSummaryData, isLoading: sessionGroupSummaryLoading } =
    useDailySessionGroupSummaryQuery(
      {
        idfSessionList: sessionIds ?? [],
        idfPlayerList,
        groupKey1: settings.groupKey1 || "",
        groupKey2: settings.groupKey2,
      },
      useSessionQuery && !!idfPlayerList?.length && !!settings.groupKey1,
    )
  const groupSummaryData = useSessionQuery
    ? sessionGroupSummaryData
    : useDailyActivityQuery
      ? dailyActivityGroupSummaryData
      : trendGroupSummaryData
  const groupSummaryLoading = useSessionQuery
    ? sessionGroupSummaryLoading
    : useDailyActivityQuery
      ? dailyActivityGroupSummaryLoading
      : trendGroupSummaryLoading

  const metricMap = useMemo(() => {
    return (
      metricList?.reduce((map, obj) => {
        map.set(obj.key, obj)

        return map
      }, new Map()) || new Map()
    )
  }, [metricList])

  const displayColumns = useMemo(
    () => buildCustomTableDisplayColumns(settings, reportType),
    [settings, reportType],
  )

  const tableHeader = useMemo((): TableHeader[] => {
    if (!metricList || !selectedUnitList) {
      return []
    }

    return buildCustomTableHeader(settings, displayColumns, metricMap, selectedUnitList)
  }, [metricList, selectedUnitList, settings, displayColumns, metricMap])

  const tableBody = useMemo((): Array<Array<string | number | null>> => {
    if (!groupSummaryData || isEmpty(groupSummaryData) || tableHeader.length === 0) {
      return []
    }

    return buildCustomTableBody(settings, groupSummaryData, tableHeader, displayColumns, metricMap)
  }, [groupSummaryData, tableHeader, settings, displayColumns, metricMap])

  // 행별 원본 Summary(calcType 소스) — 요약 행 집계는 포맷된 셀 문자열이 아니라 이 원값으로 한다
  const rowSources = useMemo((): CustomTableRowSource[] => {
    if (!groupSummaryData || isEmpty(groupSummaryData) || tableHeader.length === 0) {
      return []
    }

    return buildCustomTableRowSources(settings, groupSummaryData)
  }, [groupSummaryData, tableHeader.length, settings])

  return {
    tableHeader,
    tableBody,
    rowSources,
    metricMap,
    selectedUnitList,
    calculatedMetrics: (calculatedMetrics ?? undefined) as CalculatedMetric[] | undefined,
    displayColumnCount: displayColumns.length,
    isLoading: groupSummaryLoading,
  }
}

// ─── Total / Average 요약 행 (일감 #98) ──────────────────────────────────────
// 화면(CustomTableSection)·PDF(PrintableCustomTableSection)·CSV(customTableCsv) 가 같은 빌더를 쓴다.
// 집계 규칙은 summaryAggregate.ts(서버 Summary.java 이식) 하나에만 둔다.

export const SUMMARY_ROW_ID = { total: "summary-total", average: "summary-average" } as const

export type SummaryRowKind = "total" | "average"
const SUMMARY_ROW_ORDER: SummaryRowKind[] = ["total", "average"]

/**
 * 템플릿 설정의 summaryRows → 표시할 요약 행 종류(위→아래 순서: Total, Average) (#155).
 * 필드가 없는 기존 Custom Table 은 빈 배열(미표시). 신규 Table·Stats 대치 Table 은 생성·변환 시 둘 다 true 로 채운다.
 */
export function resolveSummaryRowKinds(
  summaryRows: SummaryRowsSetting | undefined | null,
): SummaryRowKind[] {
  if (!summaryRows) {
    return []
  }

  return SUMMARY_ROW_ORDER.filter((kind) => summaryRows[kind] === true)
}

/** 행의 원본 Summary 와, 다이브(GK 전용) 지표를 이 행에서 유효하게 볼지 여부 */
export type CustomTableRowSource = { source: SummaryImpl; isGK: boolean }

/** 그룹 기준이 선수·포지션이면 GK 행만 다이브 지표를 갖는다 — buildCustomTableBody 의 null 처리와 동일 규칙 */
export function isCustomTableRowGK(settings: StatsSettings, item: TrendGroupSummaryRow): boolean {
  const { groupKey1, groupKey2 } = settings
  if (groupKey1 === "player" || groupKey2 === "player") {
    return item.position1 === "GK"
  }
  if (groupKey1 === "position1" || groupKey2 === "position1") {
    return item.position1 === "GK"
  }
  if (groupKey1 === "position2" || groupKey2 === "position2") {
    return item.position2 === "GK"
  }

  return true
}

/** tableBody 와 같은 순서의 행별 원본 Summary (calcType 에 따라 total/average 소스) */
export function buildCustomTableRowSources(
  settings: StatsSettings,
  groupSummaryData: TrendGroupSummaryRow[],
): CustomTableRowSource[] {
  return groupSummaryData.map((item) => ({
    source: parseSummary(settings.calcType === "total" ? item.total : item.average),
    isGK: isCustomTableRowGK(settings, item),
  }))
}

/**
 * 요약 2행(Total, Average)을 tableBody 와 같은 배열 형태로 만든다. 표시 컬럼(그룹·시간·건수)은
 * 첫 컬럼에 라벨, 나머지는 빈 문자열. 데이터 0건이면 빈 배열(요약 행 미표시 정책 ⑤).
 */
export function buildCustomTableSummaryRows({
  tableHeader,
  displayColumnCount,
  rowSources,
  metricMap,
  selectedUnitList,
  calculatedMetrics,
  t,
  kinds = SUMMARY_ROW_ORDER,
}: {
  tableHeader: TableHeader[]
  displayColumnCount: number
  rowSources: CustomTableRowSource[]
  metricMap: MetricMapType
  selectedUnitList?: Unit[]
  calculatedMetrics?: CalculatedMetric[]
  t: TFunction | ((key: string) => string)
  /** 만들 요약 행 종류 — resolveSummaryRowKinds(settings.summaryRows). 비면 요약 행 없음 (#155) */
  kinds?: SummaryRowKind[]
}): Array<Array<string | number | null>> {
  if (!rowSources.length || !tableHeader.length || !kinds.length) {
    return []
  }

  const metricHeaders = tableHeader.slice(displayColumnCount)
  const metricKeys = metricHeaders.map((header) => header.key)
  const diveKeys = metricKeys.filter((key) => DIVE_METRICS.includes(key))
  const generalKeys = metricKeys.filter((key) => !DIVE_METRICS.includes(key))

  const { total, average } = aggregateSummaryRows({
    rows: rowSources.map((row) => row.source),
    metricKeys: generalKeys,
    metricMap,
    selectedUnitList,
    calculatedMetrics,
  })

  // 다이브(GK 전용) 지표는 GK 행만으로 집계 — Daily 서버/CSV(gkPlayers) 규칙과 동일
  if (diveKeys.length) {
    const gk = aggregateSummaryRows({
      rows: rowSources.filter((row) => row.isGK).map((row) => row.source),
      metricKeys: diveKeys,
      metricMap,
      selectedUnitList,
      calculatedMetrics,
    })
    Object.assign(total, gk.total)
    Object.assign(average, gk.average)
  }

  const format = (
    header: TableHeader,
    value: number | null,
    rowCalcType: "total" | "average",
  ): string | null => {
    if (value === null || value === undefined) {
      return null
    }
    // Average 행의 횟수(times) 지표는 소수 1자리 — buildCustomTableHeader 의 calcType=average 규칙과 동일
    const decimalPoint =
      rowCalcType === "average" && header.unit === "times" ? 1 : header.decimalPoint || 0

    return getMetricValueStr({
      value,
      unit: header.unit,
      decimalPoint,
      calcType: rowCalcType,
      showUnit: false,
    })
  }

  const buildRow = (
    label: string,
    values: Record<string, number | null>,
    rowCalcType: "total" | "average",
  ) => {
    const row: Array<string | number | null> = []
    for (let index = 0; index < displayColumnCount; index++) {
      row.push(index === 0 ? label : "")
    }
    metricHeaders.forEach((header) =>
      row.push(format(header, values[header.key] ?? null, rowCalcType)),
    )

    return row
  }

  // 행 셀의 출처(settings.calcType)와 무관하게, 설정(summaryRows)에서 켠 종류만 Total → Average 순으로 낸다
  const rowsByKind: Record<SummaryRowKind, () => Array<string | number | null>> = {
    total: () => buildRow(t("total"), total, "total"),
    average: () => buildRow(t("average"), average, "average"),
  }

  return SUMMARY_ROW_ORDER.filter((kind) => kinds.includes(kind)).map((kind) => rowsByKind[kind]())
}

/**
 * 첫 열부터 연속된 고정(isFixed) 컬럼 수 — 요약 행의 Total/Average 라벨 셀은 이 폭만큼 열 병합한다 (#98).
 * 첫 열이 고정이 아니면 0 (병합 없음). 화면(DataGrid colSpan)과 PDF(td colSpan)가 같은 규칙을 쓴다.
 */
export function countLeadingFixedColumns(tableHeader: Array<{ isFixed?: boolean | null }>): number {
  let count = 0
  for (const header of tableHeader) {
    if (!header.isFixed) {
      break
    }
    count++
  }

  return count
}

/** 요약 행(배열) → 그리드 행 객체. isBold/isSummary 로 굵게·임계값 미적용·라벨 셀 처리. kinds 는 빌더에 넘긴 것과 같은 순서 */
export function mapSummaryRowsToGridRows(
  summaryRows: Array<Array<string | number | null>>,
  tableHeader: TableHeader[],
  kinds: SummaryRowKind[] = SUMMARY_ROW_ORDER,
): Array<Record<string, number | string | boolean>> {
  const orderedKinds = SUMMARY_ROW_ORDER.filter((kind) => kinds.includes(kind))

  return summaryRows.map((row, index) => {
    const gridRow: Record<string, number | string | boolean> = {
      _rowIndex: SUMMARY_ROW_ID[orderedKinds[index] ?? (index === 0 ? "total" : "average")],
      isBold: true,
      isSummary: true,
    }
    row.forEach((cell, cellIndex) => {
      gridRow[tableHeader[cellIndex]?.key as string] = cell ?? ""
    })

    return gridRow
  })
}

/** 컬럼 숫자 범위 필터 적용 — 화면(CustomTableSection)과 PDF 가 같은 술어를 쓴다 */
export function applyColumnFilters(
  rows: Array<Record<string, number | string>>,
  columnFilters: ColumnFilterMap | undefined,
): Array<Record<string, number | string>> {
  if (!columnFilters) {
    return rows
  }
  const active = Object.entries(columnFilters).filter(([, filter]) => isColumnFilterActive(filter))
  if (!active.length) {
    return rows
  }

  return rows.filter((row) => {
    for (const [columnKey, filter] of active) {
      const [min, max] = filter.range
      const cellValue = row[columnKey]
      if (cellValue === null || cellValue === undefined || cellValue === "") {
        return false
      }
      const numValue =
        typeof cellValue === "number" ? cellValue : localeStringToNumber(String(cellValue))
      if (isNaN(numValue) || numValue < min || numValue > max) {
        return false
      }
    }

    return true
  })
}

// ─── 정렬 (화면·PDF 공용, WEB-2274) ─────────────────────────────────────────
// 화면(CustomTableSection)의 정렬 로직을 그대로 이관 — PDF(PrintableCustomTableSection)가
// 동일 비교기로 정렬해 "화면에 보이는 순서 = PDF 순서"를 보장한다.

/** tableBody(배열 행) → key 기반 객체 행. _rowIndex 로 원본 행 복원 가능 */
export function mapTableBodyToRows(
  tableBody: Array<Array<string | number | null>>,
  tableHeader: TableHeader[],
): Array<Record<string, number | string>> {
  return tableBody.map((row, rowIndex) => {
    const newCell: Record<string, number | string> = { _rowIndex: rowIndex }
    row.forEach((cell, index) => {
      newCell[tableHeader[index]?.key as string] = cell ?? ""
    })

    return newCell
  })
}

/** 그룹 컬럼(날짜·포지션·주차 등) 전용 비교기 — 포지션 순서는 백엔드 rank 주입 */
export function createCompareByGroupKey(
  positionRank: (code?: string) => number,
  positionGroupRank: (group?: string) => number,
) {
  return (
    a: Record<string, number | string>,
    b: Record<string, number | string>,
    groupKey: string,
  ): number => {
    switch (groupKey) {
      case "activity": {
        const dateA = new Date(a["activityStartDate"] as string).getTime()
        const dateB = new Date(b["activityStartDate"] as string).getTime()

        if (isNaN(dateA) && isNaN(dateB)) {
          return 0
        }
        if (isNaN(dateA)) {
          return 1
        }
        if (isNaN(dateB)) {
          return -1
        }

        return dateA - dateB
      }
      case "activityType": {
        // 셀 값은 타입 이름("training" 등) — 기존 이름→키 매핑(activityType)으로 키를 얻어 순서 비교
        const typeA = String(a["activityType"] ?? "")
        const typeB = String(b["activityType"] ?? "")
        const orderA = ACTIVITY_TYPE_ORDER[activityTypeKeyByName[typeA] ?? typeA] ?? 999
        const orderB = ACTIVITY_TYPE_ORDER[activityTypeKeyByName[typeB] ?? typeB] ?? 999

        return orderA - orderB
      }
      case "matchDay": {
        const mdA = parseMatchDay(String(a["matchDay"] ?? ""))
        const mdB = parseMatchDay(String(b["matchDay"] ?? ""))

        return mdB - mdA
      }
      case "seasonWeek": {
        const weekA = parseSeasonWeek(String(a["seasonWeek"] ?? ""))
        const weekB = parseSeasonWeek(String(b["seasonWeek"] ?? ""))
        if (weekA.year !== weekB.year) {
          return weekA.year - weekB.year
        }

        return weekA.week - weekB.week
      }
      case "session": {
        const dateA = new Date(a["sessionStartDate"] as string).getTime()
        const dateB = new Date(b["sessionStartDate"] as string).getTime()
        if (isNaN(dateA) && isNaN(dateB)) {
          return 0
        }
        if (isNaN(dateA)) {
          return 1
        }
        if (isNaN(dateB)) {
          return -1
        }

        return dateA - dateB
      }
      case "sessionType": {
        return String(a["sessionType"] ?? "").localeCompare(String(b["sessionType"] ?? ""))
      }
      case "player": {
        const numA = parseInt(String(a["uniformNumber"] ?? "9999"), 10)
        const numB = parseInt(String(b["uniformNumber"] ?? "9999"), 10)

        return numA - numB
      }
      case "position1": {
        // position1 = 상위 그룹(DF/FW…) → 백엔드 그룹 순서
        const posA = String(a["position1"] ?? "").toUpperCase()
        const posB = String(b["position1"] ?? "").toUpperCase()

        return positionGroupRank(posA) - positionGroupRank(posB)
      }
      case "position2": {
        // position2 = 세부 포지션(CF/CB…) → 백엔드 포지션 순서
        const posA = String(a["position2"] ?? "").toUpperCase()
        const posB = String(b["position2"] ?? "").toUpperCase()

        return positionRank(posA) - positionRank(posB)
      }
      case "playerGroup": {
        return String(a["playerGroup"] ?? "").localeCompare(String(b["playerGroup"] ?? ""))
      }
      case "date": {
        const dateA = new Date(a["date"] as string).getTime()
        const dateB = new Date(b["date"] as string).getTime()
        if (isNaN(dateA) && isNaN(dateB)) {
          return 0
        }
        if (isNaN(dateA)) {
          return 1
        }
        if (isNaN(dateB)) {
          return -1
        }

        return dateA - dateB
      }
      case "week": {
        const weekA = parseSeasonWeek(String(a["week"] ?? ""))
        const weekB = parseSeasonWeek(String(b["week"] ?? ""))
        if (weekA.year !== weekB.year) {
          return weekA.year - weekB.year
        }

        return weekA.week - weekB.week
      }
      case "month": {
        const monthA = parseMonth(String(a["month"] ?? ""))
        const monthB = parseMonth(String(b["month"] ?? ""))
        if (monthA.year !== monthB.year) {
          return monthA.year - monthB.year
        }

        return monthA.month - monthB.month
      }
      case "year": {
        const yearA = parseInt(String(a["year"] ?? "9999"), 10)
        const yearB = parseInt(String(b["year"] ?? "9999"), 10)

        return yearA - yearB
      }
      case "position": {
        // 선수 그룹의 포지션 컬럼(세부 포지션) — Stats by Player 와 같은 백엔드 포지션 순서
        const posA = String(a["position"] ?? "").toUpperCase()
        const posB = String(b["position"] ?? "").toUpperCase()

        return positionRank(posA) - positionRank(posB)
      }
      default:
        return 0
    }
  }
}

/** 화면과 동일 규칙 정렬: 정렬 미지정 시 groupKey1/2 기본 정렬, 지정 시 해당 컬럼 정렬 */
export function sortCustomTableRows(
  rows: Array<Record<string, number | string>>,
  sort: ReportSortSetting | undefined,
  groupKey1: string | undefined,
  groupKey2: string | null | undefined,
  compareByGroupKey: ReturnType<typeof createCompareByGroupKey>,
): Array<Record<string, number | string>> {
  return [...rows].sort((a, b) => {
    if (!sort) {
      if (groupKey1) {
        const cmp1 = compareByGroupKey(a, b, groupKey1)
        if (cmp1 !== 0) {
          return cmp1
        }
      }

      if (groupKey2) {
        const cmp2 = compareByGroupKey(a, b, groupKey2)
        if (cmp2 !== 0) {
          return cmp2
        }
      }

      return 0
    }

    const field = sort.key
    const order = sort.order === "asc" ? 1 : -1
    let aValue = a[field]
    let bValue = b[field]

    if (aValue === "" || aValue === null || aValue === undefined) {
      return 1
    }
    if (bValue === "" || bValue === null || bValue === undefined) {
      return -1
    }

    // month/week/year/date/position 등 그룹 컬럼은 전용 비교기로 정렬
    // (일반 비교는 "2025 May" 같은 그룹 라벨을 시간순으로 못 맞춤)
    const groupCmp = compareByGroupKey(a, b, field)
    if (groupCmp !== 0) {
      return groupCmp * order
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      const aNum = localeStringToNumber(aValue)
      const bNum = localeStringToNumber(bValue)

      if (!isNaN(aNum) && !isNaN(bNum)) {
        aValue = aNum
        bValue = bNum
      }
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return (aValue - bValue) * order
    }

    return String(aValue).localeCompare(String(bValue)) * order
  })
}
